// src/components/PrintSafeImage.jsx
// REPLACE the entire file with this new version (V4)

import React, { useRef, useEffect, useState } from 'react';

// A simple cache to avoid reloading images
const imageCache = {};

function loadImage(src) {
  if (imageCache[src]) {
    return Promise.resolve(imageCache[src]);
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // For Mapbox, etc.
    img.onload = () => {
      imageCache[src] = img;
      resolve(img);
    };
    img.onerror = (err) => {
      console.error('Failed to load image:', src);
      reject(err);
    };
    img.src = src;
  });
}

/**
 * Renders a "print-safe" image (Version 4)
 * - This version contains the correct "object-fit: cover" math
 * for the canvas, fixing the distortion in tall containers.
 */
export default function PrintSafeImage({
  src,
  position, // The { x, y, scale } object
  isPrinting = false,
  style = {},
  className = '',
  alt = '',
}) {
  const canvasRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  // This effect runs when isPrinting=true to draw the image on the canvas
  useEffect(() => {
    // We run this effect whenever the props change,
    // or if it was loading and printing is still true
    if (isPrinting && src) {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      setIsLoading(true);
      
      loadImage(src)
        .then((img) => {
          const { naturalWidth, naturalHeight } = img;
          const { width, height } = canvas.getBoundingClientRect();
          
          if (width === 0 || height === 0) {
            setIsLoading(false);
            return;
          }

          canvas.width = width;
          canvas.height = height;

          const pos = position || { x: 50, y: 50, scale: 100 };
          const scale = (pos.scale || 100) / 100;
          
          // --- This is the new, correct "object-fit: cover" math ---

          // 1. Calculate aspect ratios
          const imgRatio = naturalWidth / naturalHeight;
          const containerRatio = width / height;

          // 2. Calculate "cover" dimensions
          let renderW, renderH;
          if (imgRatio > containerRatio) {
            // Image is wider than container, fill by height
            renderH = height;
            renderW = height * imgRatio;
          } else {
            // Image is taller than container, fill by width
            renderW = width;
            renderH = width / imgRatio;
          }

          // 3. Apply scale (zoom)
          renderW *= scale;
          renderH *= scale;

          // 4. Calculate translation
          let offX = (width - renderW) / 2; // Base centering
          let offY = (height - renderH) / 2; // Base centering
          offX += (width * (pos.x - 50) / 100); // User pan X
          offY += (height * (pos.y - 50) / 100); // User pan Y

          // 5. Apply filter if any
          if (style.filter) {
            ctx.filter = style.filter;
          }
          
          // 6. Draw
          ctx.drawImage(img, offX, offY, renderW, renderH);

          setIsLoading(false);
        })
        .catch((err) => {
          console.error('Error drawing image to canvas:', err);
          setIsLoading(false);
        });
    }
  }, [isPrinting, src, position, style]); // Rerun if any of these change

  // Render fast CSS version for live preview
  // This uses the 'style' prop passed from the template
  if (!isPrinting) {
    return (
      <img
        src={src}
        alt={alt}
        style={style}
        className={className}
      />
    );
  }

  // Render <canvas> version for printing
  return (
    <>
      <canvas
        ref={canvasRef}
        className={className}
        style={{ width: '100%', height: '100%', display: 'block' }}
      />
      {isLoading && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#999', fontSize: 12 }}>...</div>
      )}
    </>
  );
}